import { useCallback, useRef } from 'react'

export function useDebouncedCallback<T extends any[]>(
  fn: (...args: T) => any,
  delay: number,
  { maxWait, leading = false, leadingTimeout = undefined }: { maxWait?: number; leading?: boolean; leadingTimeout?: number } = {}
): [debounced: (...args: Parameters<typeof fn>) => void, cancel: () => void, callPending: () => void] {
  const maxWaitHandler = useRef<number>(null)
  const maxWaitArgs = useRef<T | null>(null)

  const wasLeadingCalled = useRef(false)
  const leadingBurstLock = useRef(false)

  const functionTimeoutHandler = useRef<number>(null)

  const debouncedFunction = useRef(fn)
  debouncedFunction.current = fn

  const cancelDebouncedCallback = useCallback(() => {
    if (functionTimeoutHandler.current) window.clearTimeout(functionTimeoutHandler.current)
    if (maxWaitHandler.current) window.clearTimeout(maxWaitHandler.current)
    maxWaitHandler.current = null
    maxWaitArgs.current = null
    functionTimeoutHandler.current = null
    wasLeadingCalled.current = false
  }, [])

  const debouncedCallback = useCallback(
    (...args: T) => {
      maxWaitArgs.current = args
      if (functionTimeoutHandler.current) window.clearTimeout(functionTimeoutHandler.current)

      if (leadingBurstLock.current) return

      if (!functionTimeoutHandler.current && leading && !wasLeadingCalled.current) {
        debouncedFunction.current(...args)

        wasLeadingCalled.current = true
        setTimeout(
          () => {
            wasLeadingCalled.current = false
          },
          (leadingTimeout || 0) + delay
        )

        if (leadingTimeout) {
          leadingBurstLock.current = true
          setTimeout(() => {
            leadingBurstLock.current = false
          }, leadingTimeout)
        }
        return
      }

      functionTimeoutHandler.current = window.setTimeout(() => {
        cancelDebouncedCallback()

        debouncedFunction.current(...args)
      }, delay)

      if (maxWait && !maxWaitHandler.current) {
        maxWaitHandler.current = window.setTimeout(() => {
          const args = maxWaitArgs.current
          cancelDebouncedCallback()

          debouncedFunction.current(...args!)
        }, maxWait)
      }
    },
    [maxWait, delay, cancelDebouncedCallback, leading, leadingTimeout]
  )

  const callPending = () => {
    if (!functionTimeoutHandler.current) return

    debouncedFunction.current(...maxWaitArgs.current!)
    cancelDebouncedCallback()
  }

  return [debouncedCallback, cancelDebouncedCallback, callPending]
}
